import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import DataTable from '@/components/admin/DataTable';
import {
  ConfirmDeleteDialog,
  GRADE_COLORS,
  GPA_MAP,
  InlineSelect,
  LETTER_GRADES,
  courseLabel,
  gradeStatusOptions,
} from '@/components/admin/AcademicTableHelpers';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  createCourseGrade,
  deleteCourseGrade,
  getCourseGrades,
  getCourseTerms,
  getCourses,
  getStudents,
  updateCourseGrade,
} from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { GraduationCap, Plus, Trash2 } from 'lucide-react';

function filterParam(value) {
  return value === 'all' ? undefined : value;
}

function gradeOptions() {
  return LETTER_GRADES.map((grade) => ({
    value: grade,
    label: GPA_MAP[grade] !== undefined ? `${grade} (${GPA_MAP[grade].toFixed(2)})` : grade,
  }));
}

export default function AdminGrades() {
  const { loading: authLoading } = useAuth();
  const [grades, setGrades] = useState([]);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [termFilter, setTermFilter] = useState('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [studentFilter, setStudentFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [savingInline, setSavingInline] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ student_id: '', course_id: '', letter_grade: 'B', status: 'Completed', term: '' });

  const loadData = async () => {
    try {
      setLoading(true);
      const [gradeRes, courseRes, studentRes, termRes] = await Promise.all([
        getCourseGrades({
          student_id: filterParam(studentFilter),
          course_id: filterParam(courseFilter),
          term: filterParam(termFilter),
        }),
        getCourses(),
        getStudents(),
        getCourseTerms(),
      ]);
      const nextGrades = gradeRes.data || [];
      setGrades(nextGrades);
      setCourses(courseRes.data || []);
      setStudents(studentRes.data || []);
      setTerms(termRes.data || []);
      setSelectedIds((prev) => {
        const visible = new Set(nextGrades.map((grade) => grade.id));
        return new Set([...prev].filter((id) => visible.has(id)));
      });
    } catch (err) {
      console.error(err);
      toast.error('Could not load grades');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, studentFilter, courseFilter, termFilter]);

  const courseMap = useMemo(() => {
    const map = {};
    courses.forEach((course) => { map[course.id] = course; });
    return map;
  }, [courses]);

  const studentMap = useMemo(() => {
    const map = {};
    students.forEach((student) => { map[student.id] = student; });
    return map;
  }, [students]);

  const filteredGrades = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return grades;
    return grades.filter((grade) => {
      const student = studentMap[grade.student_id];
      const course = courseMap[grade.course_id];
      const gradeCourse = `${grade.course_code ? `${grade.course_code} - ` : ''}${grade.course_title || ''}`;
      return (
        (student?.name || '').toLowerCase().includes(query)
        || (student?.email || '').toLowerCase().includes(query)
        || courseLabel(course).toLowerCase().includes(query)
        || gradeCourse.toLowerCase().includes(query)
        || (grade.term || '').toLowerCase().includes(query)
      );
    });
  }, [courseMap, grades, search, studentMap]);

  const selectedGrades = useMemo(
    () => grades.filter((grade) => selectedIds.has(grade.id)),
    [grades, selectedIds],
  );

  const openCreate = () => {
    setForm({ student_id: '', course_id: '', letter_grade: 'B', status: 'Completed', term: '' });
    setShowDialog(true);
  };

  const handleCourseSelect = (courseId) => {
    const course = courses.find((item) => item.id === courseId);
    setForm((prev) => ({ ...prev, course_id: courseId, term: course?.term || prev.term }));
  };

  const handleSave = async () => {
    if (!form.student_id || !form.course_id || !form.letter_grade) {
      toast.error('Student, course, and grade are required');
      return;
    }
    try {
      const course = courses.find((item) => item.id === form.course_id);
      await createCourseGrade({ ...form, term: form.term || course?.term || '' });
      toast.success('Grade assigned');
      setShowDialog(false);
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to save grade');
    }
  };

  const handleInlineUpdate = async (grade, payload, label) => {
    try {
      setSavingInline(`${grade.id}:${label}`);
      const { data } = await updateCourseGrade(grade.id, payload);
      setGrades((prev) => prev.map((item) => (item.id === grade.id ? data : item)));
      toast.success('Grade updated');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || `Could not update ${label}`);
      await loadData();
    } finally {
      setSavingInline('');
    }
  };

  const confirmDelete = (items, type = 'single') => {
    setPendingDelete({ type, grades: items });
  };

  const handleConfirmedDelete = async () => {
    if (!pendingDelete) return;
    try {
      setDeleting(true);
      const ids = pendingDelete.grades.map((grade) => grade.id);
      await Promise.all(ids.map((id) => deleteCourseGrade(id)));
      toast.success(`${ids.length} grade record${ids.length === 1 ? '' : 's'} deleted`);
      setPendingDelete(null);
      setSelectedIds(new Set());
      await loadData();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not delete grade records');
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      id: 'student',
      header: 'Student',
      sortable: true,
      sortAccessor: (grade) => studentMap[grade.student_id]?.name || '',
      cellClassName: 'min-w-[230px]',
      cell: (grade) => {
        const student = studentMap[grade.student_id];
        return (
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#062B1A]/5 flex items-center justify-center">
              <GraduationCap className="w-4 h-4 text-[#062B1A]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#062B1A]">{student?.name || 'Unknown student'}</p>
              <p className="text-xs text-[#062B1A]/45">{student?.email || 'No email'}</p>
            </div>
          </div>
        );
      },
    },
    {
      id: 'course',
      header: 'Course',
      sortable: true,
      sortAccessor: (grade) => grade.course_title || courseLabel(courseMap[grade.course_id]),
      cellClassName: 'min-w-[260px] text-sm text-[#062B1A]/70',
      cell: (grade) => {
        const fallback = courseLabel(courseMap[grade.course_id]);
        return `${grade.course_code ? `${grade.course_code} - ` : ''}${grade.course_title || fallback}`;
      },
    },
    {
      id: 'term',
      header: 'Term',
      sortable: true,
      cellClassName: 'min-w-[130px] text-sm text-[#062B1A]/70',
      cell: (grade) => grade.term || '-',
    },
    {
      id: 'letter_grade',
      header: 'Letter grade',
      sortable: true,
      cellClassName: 'min-w-[170px]',
      cell: (grade) => (
        <div className="flex items-center gap-2">
          <Badge className={`text-xs border-0 ${GRADE_COLORS[grade.letter_grade] || 'bg-gray-100 text-gray-700'}`}>
            {grade.letter_grade}
          </Badge>
          <InlineSelect
            value={grade.letter_grade || 'B'}
            disabled={savingInline === `${grade.id}:letter_grade`}
            options={gradeOptions()}
            className="w-[112px]"
            onChange={(value) => handleInlineUpdate(grade, { letter_grade: value }, 'letter_grade')}
          />
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      cellClassName: 'min-w-[165px]',
      cell: (grade) => (
        <InlineSelect
          value={grade.status || 'Completed'}
          disabled={savingInline === `${grade.id}:status`}
          options={gradeStatusOptions}
          onChange={(value) => handleInlineUpdate(grade, { status: value }, 'status')}
        />
      ),
    },
    {
      id: 'credits',
      header: 'Credits',
      sortable: true,
      sortAccessor: (grade) => Number(grade.credits || 0),
      cellClassName: 'min-w-[100px] text-sm text-[#062B1A]/70',
      cell: (grade) => grade.credits || courseMap[grade.course_id]?.credits || 3,
    },
  ];

  const rowActions = (grade) => (
    <Button type="button" variant="outline" size="sm" className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" onClick={() => confirmDelete([grade])}>
      <Trash2 className="w-4 h-4 mr-2" />
      Delete
    </Button>
  );

  const bulkActions = ({ selectedCount }) => (
    <Button
      type="button"
      size="sm"
      disabled={selectedCount === 0}
      className="bg-red-700 text-white hover:bg-red-800"
      onClick={() => confirmDelete(selectedGrades, 'bulk')}
    >
      <Trash2 className="w-4 h-4 mr-2" />
      Bulk Delete
    </Button>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Course Grades</h2>
          <p className="text-sm text-[#062B1A]/60 mt-1">Assign and maintain final letter grades by student and course.</p>
        </div>
        <Button className="bg-[#062B1A] text-white hover:bg-[#041B10]" onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Assign Grade
        </Button>
      </div>

      <DataTable
        title="Grade Management"
        description="Grades use inline editing for letter grade and status. Supporting student and course names are resolved automatically."
        data={filteredGrades}
        columns={columns}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search students, courses, or terms..."
        filters={[
          { id: 'student', value: studentFilter, onChange: setStudentFilter, options: [{ value: 'all', label: 'All students' }, ...students.map((student) => ({ value: student.id, label: student.name || student.email }))] },
          { id: 'course', value: courseFilter, onChange: setCourseFilter, options: [{ value: 'all', label: 'All courses' }, ...courses.map((course) => ({ value: course.id, label: courseLabel(course) }))] },
          { id: 'term', value: termFilter, onChange: setTermFilter, options: [{ value: 'all', label: 'All terms' }, ...terms.map((term) => ({ value: term, label: term }))] },
        ]}
        selectedIds={selectedIds}
        onSelectedIdsChange={setSelectedIds}
        bulkActions={bulkActions}
        rowActions={rowActions}
        emptyMessage="No grades match the current filters."
        pageSize={8}
      />

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>Assign Grade</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Student *</label>
              <Select value={form.student_id} onValueChange={(value) => setForm((prev) => ({ ...prev, student_id: value }))}>
                <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue placeholder="Select student" /></SelectTrigger>
                <SelectContent>{students.map((student) => <SelectItem key={student.id} value={student.id}>{student.name} ({student.email})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Course *</label>
              <Select value={form.course_id} onValueChange={handleCourseSelect}>
                <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue placeholder="Select course" /></SelectTrigger>
                <SelectContent>{courses.map((course) => <SelectItem key={course.id} value={course.id}>{courseLabel(course)}{course.term ? ` (${course.term})` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Letter Grade *</label>
                <Select value={form.letter_grade} onValueChange={(value) => setForm((prev) => ({ ...prev, letter_grade: value }))}>
                  <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue /></SelectTrigger>
                  <SelectContent>{gradeOptions().map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Status</label>
                <Select value={form.status} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
                  <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue /></SelectTrigger>
                  <SelectContent>{gradeStatusOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#062B1A]/70 mb-1.5 block">Term</label>
                <Select value={form.term || '_none'} onValueChange={(value) => setForm((prev) => ({ ...prev, term: value === '_none' ? '' : value }))}>
                  <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue placeholder="Auto" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Auto from course</SelectItem>
                    {terms.map((term) => <SelectItem key={term} value={term}>{term}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" onClick={handleSave}>Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.grades?.length || 0} grade record${pendingDelete?.grades?.length === 1 ? '' : 's'}?`}
        description="This permanently removes the selected course grade record."
        count={pendingDelete?.grades?.length || 0}
        deleting={deleting}
        onConfirm={handleConfirmedDelete}
      />
    </div>
  );
}
